import { calculateFinalPrice, decimalPriceToFloat } from '@inkitt/utils-conversions';
import type Stripe from 'stripe';
import { GTV_LIFETIME_SKU } from './constants';
import { PurchaseType } from './types/PurchaseType';

// Moved to @inkitt/utils-conversions (client-safe); re-exported so existing consumers of this
// barrel keep working.
export { decimalPriceToFloat };

export const localizedPriceAmountWithDecimal = ({
  amountInCents,
  currency,
  countryCode,
  languageCode,
}: {
  amountInCents: number | null;
  languageCode: string;
  currency: string;
  countryCode: string;
}) => {
  const sanitizedLanguageCode = languageCode === 'pt-BR' ? 'pt' : languageCode;
  const formatter = new Intl.NumberFormat(`${sanitizedLanguageCode.toLowerCase()}-${countryCode.toUpperCase()}`, {
    style: 'currency',
    currency: currency.toUpperCase(),
  });

  return formatter.format(decimalPriceToFloat({ decimalPrice: amountInCents || 0, currency }));
};

export const getFinalPriceAmountInCentsAfterCoupon = ({
  price,
  coupon,
}: {
  price: Stripe.Price;
  coupon?: Stripe.Coupon;
}) => {
  const originalPriceInCents = price.unit_amount || 0;
  const finalAnnualAmountInCents = coupon?.percent_off
    ? calculateFinalPrice({
        originalPrice: originalPriceInCents,
        discountPercentage: coupon.percent_off,
      })
    : originalPriceInCents;
  return { originalPriceInCents, finalAnnualAmountInCents };
};

export const getFinalPriceAmountAfterCoupon = ({
  amount,
  coupon,
  currency,
  floor = true,
}: {
  amount: number | null;
  coupon?: Stripe.Coupon;
  currency: string;
  floor?: boolean;
}): number => {
  if (!amount) {
    return 0;
  }

  if (!coupon) {
    return amount;
  }

  const currencyOption = coupon.currency_options?.[currency.toLowerCase()];
  if (coupon.amount_off && currencyOption) {
    return amount - currencyOption.amount_off;
  }

  if (coupon.percent_off) {
    const discount = amount * (coupon.percent_off / 100);
    if (floor) {
      return Math.floor(amount - discount);
    }
    return amount - discount;
  }

  return amount;
};

export const isMonthlyPrice = ({ price }: { price: Stripe.Price }): boolean => {
  return price.recurring?.interval === 'month' && price.recurring?.interval_count === 1;
};

export const isYearlyPrice = ({ price }: { price: Stripe.Price }): boolean => {
  return price.recurring?.interval === 'year' && price.recurring?.interval_count === 1;
};

export const isWeeklyPrice = ({ price }: { price: Stripe.Price }): boolean => {
  return price.recurring?.interval === 'week' && price.recurring?.interval_count === 1;
};

const LIFETIME_SKUS = ['glt-un-lt', GTV_LIFETIME_SKU];

export const isLifetimeSku = ({ sku }: { sku: string }): boolean => {
  return LIFETIME_SKUS.includes(sku);
};

export const skuToProductAndEntitlementProduct = (
  sku: string | undefined,
): { product: 'harlequin' | undefined; entitlementProduct: 'harlequin' | 'galatea' } => {
  const product = sku?.startsWith('h') ? 'harlequin' : undefined;
  const entitlementProduct = product ?? 'galatea';
  return {
    product,
    entitlementProduct,
  };
};

export const getPurchaseType = ({ price, sku }: { price: Stripe.Price; sku?: string }): PurchaseType | undefined => {
  if (isYearlyPrice({ price })) {
    return 'yearly';
  }

  if (isMonthlyPrice({ price })) {
    return 'monthly';
  }

  if (isWeeklyPrice({ price })) {
    return 'weekly';
  }

  if (sku && isLifetimeSku({ sku })) {
    return 'lifetime';
  }

  if (price.type === 'one_time') {
    return 'one_time';
  }

  return undefined;
};

export const getWeeklyPrice = ({ amount, type }: { amount: number; type: PurchaseType }): number | undefined => {
  if (type === 'weekly') {
    return amount;
  }

  if (type === 'yearly') {
    return Math.floor(amount / 52);
  }

  return undefined;
};
